import React, { useState, useEffect, useMemo } from 'react';
import QtyInput from './QtyInput';
import Hint from './Hint';
import MenuPricingAdvisor from './MenuPricingAdvisor';
import { computePriceAverages } from '../utils/itemPrices';
import { findPriceByName } from '../utils/ingredientMatch';
import { useLang } from '../i18n/LangContext';
import BiName, { buildZhMap, zhOf } from '../i18n/BiName';
import { usePinyinReady } from '../i18n/pron';   // V127 인쇄용 병음 미리 받기
import { printRecipes } from '../utils/recipePrint';
import { findBrokenSemiRefs } from '../utils/semiRename';   // V141 끊긴 반제품 연결
import { normalizeIngredientName } from '../utils/ingredientMatch';
import { familyRootId } from '../utils/recipeCopy';   // V150 버전 묶음
import SemiClashCard from './SemiClashCard';   // V153 반제품·식재료 이름 겹침
import PrintPicker from './PrintPicker';   // V154 레시피 + 단가표 골라서 인쇄
import { usableG } from '../utils/priceYield';   // V152 실사용량 (신선식품 로스 제외)

function RecipeView({ item, prices, onEdit, isMobile, onBack, terms = {}, user,
                     profitLoss = {}, payrollSettings = {}, onSettingsChange,
                     allRecipes = [], onFillAllZh, onRelinkSemi,
                     onCopy, onOpenRecipe,   // V150 복사 · 다른 버전 열기
                     onFixSemiClash, lastOrderOf,   // V153 이름 겹침 고치기
                     categories = null }) {   // V154 인쇄 고르기 (카테고리별)
  const T = {
    recipeItem: '메뉴', ingredient: '식재료',
    servingLabel: '인분', servingUnit: 'g', qtyUnit: 'g',
    lossRateEnabled: false,
    typeLabels: { semi: '반제품', menu: '메뉴' },
    costCardLabel: (s) => `${s}인분 원가`,
    costCardSub: '1인분 원가',
    servingBaseValue: (item) => `${item.serving_g}g`,
    totalOutputValue: (item) => `${item.total_output}g`,
    calcRatio: (recipe, value) =>
      recipe.serving_g > 0 ? (recipe.serving_g * value) / recipe.total_output : value,
    emptySlateIcon: '📋',
    emptySlateText: '왼쪽에서 레시피를 선택하세요',
    ...terms,
  };

  const [servings, setServings] = useState(1);
  // ── 원가율 시뮬레이터 ────────────────────────────────────────────────────
  // priceMode: 'registered' = 단가표 등록가 (기본)
  //            'avg30'      = 내 평균가 (지난 30일 영수증)
  const [priceMode, setPriceMode] = useState('registered');
  const [targetCostRatio, setTargetCostRatio] = useState(30);  // 목표 원가율 %
  const [priceAverages, setPriceAverages] = useState(new Map());

  useEffect(() => {
    if (!user?.uid || !prices || prices.length === 0) return;
    let cancelled = false;
    computePriceAverages(user.uid, prices, 30).then(map => {
      if (!cancelled) setPriceAverages(map);
    }).catch(e => console.warn('priceAverages failed:', e));
    return () => { cancelled = true; };
  }, [user?.uid, prices?.length]);

  // ── V109: 한중 병기 + 주방 인쇄 ─────────────────────────────────────────
  // 재료 중국어는 단가표(name_zh)에서, 메뉴명·메모 중국어는 레시피 자체에서.
  const { t, lang, fmt: money } = useLang();
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);   // V147
  // V122: 반제품(삶은 닭·염지제 등)은 단가표 줄에 중국어가 없어도 레시피 중국어 이름으로 병기
  const zhMap = useMemo(() => buildZhMap(prices || [], allRecipes || []), [prices, allRecipes]);
  // V141: 반제품 레시피가 없는 식재료(이름을 바꾸기 전 옛 이름 등) — 경고 + 다시 잇기
  const brokenSemi = useMemo(() => findBrokenSemiRefs(allRecipes || [], prices || []), [allRecipes, prices]);
  const [relinkPick, setRelinkPick] = useState({});
  const relink = (oldName, targetId, usedIn) => {
    const target = (allRecipes || []).find(r => r.id === targetId);
    if (!target || !onRelinkSemi) return;
    const msg = lang === 'zh'
      ? `把用到「${oldName}」的配方（${usedIn.join('、')}）改成「${target.name}${target.name_zh ? ' / ' + target.name_zh : ''}」。\n单价表里旧的「${oldName}」会被整理掉。\n\n继续吗？`
      : `'${oldName}'을(를) 쓰는 레시피(${usedIn.join(', ')})를 '${target.name}'(으)로 바꿉니다.\n단가표의 옛 '${oldName}' 줄은 정리돼요.\n\n계속할까요?`;
    if (!window.confirm(msg)) return;
    onRelinkSemi(oldName, targetId);
  };
  usePinyinReady(lang !== 'zh');   // V127: 인쇄 누르기 전에 병음 사전을 받아 둔다
  const [zhBusy, setZhBusy] = useState(false);
  const [zhMsg, setZhMsg] = useState('');

  const openPrint = (list, opts) => {
    if (!printRecipes(list, { zhMap, lang, ...opts })) {
      alert(t('팝업이 차단되었습니다. 팝업을 허용해주세요'));
    }
  };
  const missingRecipeZh = (allRecipes || []).filter(r =>
    (String(r?.name || '').trim() && !r.name_zh) || (String(r?.note || '').trim() && !r.note_zh)
  ).length;
  // V122: 레시피 식재료 중 중국어가 없는 것도 일괄 채우기 대상 (예전엔 레시피 이름·메모만 채워서 식재료는 그대로였다)
  const missingIngZh = useMemo(() => {
    const s = new Set();
    for (const r of allRecipes || []) {
      for (const ing of r?.ingredients || []) {
        const n = String(ing?.name || '').trim();
        if (n && /[가-힣]/.test(n) && !zhOf(zhMap, n)) s.add(n);
      }
    }
    return s.size;
  }, [allRecipes, zhMap]);
  const missingZh = missingRecipeZh + missingIngZh;
  const FAIL_WHAT = { name: '이름', note: '메모', cat: '카테고리', ingredient: '식재료' };
  const runFillZh = async () => {
    if (!onFillAllZh || zhBusy) return;
    setZhBusy(true); setZhMsg('');
    try {
      const r = await onFillAllZh();
      const failed = Array.isArray(r.failed) ? r.failed : [];
      const failedText = failed.slice(0, 4).map(f => `${f.name}(${t(FAIL_WHAT[f.what] || f.what)})`).join(', ')
        + (failed.length > 4 ? ` +${failed.length - 4}` : '');
      setZhMsg(r.total === 0
        ? t('모든 레시피에 중국어가 있습니다')
        : `${t('중국어 채움')} ${r.filled}/${r.total}` + (failed.length ? ` · ${t('못 채운 것')}: ${failedText}` : ''));
    } catch (e) {
      setZhMsg(t('번역 실패 — 네트워크를 확인하세요'));
    } finally {
      setZhBusy(false);
    }
  };
  // V154: 전체 인쇄 — 레시피와 단가표를 골라서 (몇 개는 빼고)
  const [pickOpen, setPickOpen] = useState(false);
  const picker = pickOpen ? (
    <PrintPicker categories={categories || [{ name: '', items: allRecipes || [] }]} prices={prices || []} isMobile={isMobile}
      onClose={() => setPickOpen(false)} initial={{ recipes: true, prices: true }} />
  ) : null;
  const tbBtn = {
    padding: '7px 12px', borderRadius: 8, border: '1px solid #E4E4E7', background: '#fff',
    color: '#52525B', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };
  const toolbar = (allRecipes && allRecipes.length > 0) ? (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
      <button type="button" style={tbBtn} data-print-all
        onClick={() => setPickOpen(true)}>
        🖨 {Z('전체 인쇄 (레시피 · 단가표)', '全部打印（配方 · 单价表）')} ({allRecipes.length})
      </button>
      {onFillAllZh && missingZh > 0 && (
        <button type="button" style={{ ...tbBtn, color: '#1A56A0', opacity: zhBusy ? 0.6 : 1 }}
          onClick={runFillZh} disabled={zhBusy}>
          {zhBusy ? t('번역 중...') : `中 ${t('중국어 일괄 채우기')} (${missingZh})`}
        </button>
      )}
      {zhMsg && <span style={{ fontSize: 12, color: '#71717A' }}>{zhMsg}</span>}
    </div>
  ) : null;

  if (!item) {
    return (
      <div style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                   flexDirection:'column', color:'#A1A1AA'}}>
        <div style={{fontSize:'48px', marginBottom:'12px'}}>{T.emptySlateIcon}</div>
        <p>{t(T.emptySlateText)}</p>
        {toolbar && <div style={{ marginTop: 20 }}>{toolbar}</div>}
        {picker}
      </div>
    );
  }

  // 기준량 원가 계산 — priceMode에 따라 다른 단가 적용
  const getEffectivePrice = (price) => {
    if (priceMode === 'avg30') {
      const stat = priceAverages.get(price.code);
      if (stat && stat.count > 0 && stat.avg > 0) return stat.avg;
    }
    return price.price;  // 기본: 등록가
  };
  // V125: 단가표에 있고 기준량도 있는데 단가가 0 → 빠진 재료가 아니라 무료 재료(물 등)
  const isFreeIng = (ingredient) => {
    const p = findPriceByName(prices, ingredient.name);
    return !!p && Number(p.net_g) > 0 && p.price !== '' && p.price != null && Number(p.price) === 0;
  };
  const calcCost = (ingredient) => {
    const price = findPriceByName(prices, ingredient.name);
    const base = usableG(price);   // V152: 신선식품은 로스를 뺀 실사용량, 공산품은 NET 중량
    if (!price || !base) return 0;
    const effPrice = getEffectivePrice(price);
    return (ingredient.qty / base) * effPrice;
  };

  const totalCost = item.ingredients.reduce((sum, ing) => sum + calcCost(ing), 0);
  // ── 비중 계산용: 표시되는 스케일 원가 합계 (식음료=N인분, 인테리어=계약수량·손실률 반영) ──
  const totalScaledCost = item.ingredients.reduce((sum, ing) => {
    const c = calcCost(ing);
    if (!c) return sum;
    const ratio = T.calcRatio(item, servings);
    return sum + (T.lossRateEnabled
      ? c * ratio * (1 + (ing.lossRate || 0) / 100)
      : c * ratio);   // V124: N인분 원가 (예전 c * servings 는 기준량 원가 × N 이었다)
  }, 0);
  const costPerUnit = item.serving_g > 0 ? (totalCost / item.total_output) * item.serving_g : totalCost;
  // V124: 표 머리말 "1인분" — 예전엔 단위(g)가 붙어 "1g"으로 보였다. 단위가 g가 아닌 업종은 "1대"·"1건"
  const servingWord = T.servingUnit && T.servingUnit !== 'g' ? T.servingUnit : t(T.servingLabel);
  const servingHead = `${servings}${servingWord}`;

  const sellingPrice = item.selling_price || 0;
  const costRatio = sellingPrice > 0 ? (costPerUnit / sellingPrice) * 100 : null;
  const profit = sellingPrice > 0 ? sellingPrice - costPerUnit : null;

  // 시뮬레이터 — 목표 원가율 유지하려면 권장 판매가
  const recommendedPrice = targetCostRatio > 0 ? Math.round(costPerUnit / (targetCostRatio / 100)) : null;
  // 등록가 기반 원가 (비교용)
  const totalCostRegistered = item.ingredients.reduce((sum, ing) => {
    const p = findPriceByName(prices, ing.name);
    if (!p || !usableG(p)) return sum;
    return sum + (ing.qty / usableG(p)) * p.price;   // V152
  }, 0);
  const costPerUnitRegistered = item.serving_g > 0
    ? (totalCostRegistered / item.total_output) * item.serving_g
    : totalCostRegistered;
  const costDiff = costPerUnit - costPerUnitRegistered;
  const costDiffPct = costPerUnitRegistered > 0 ? (costDiff / costPerUnitRegistered) * 100 : 0;

  // 평균가 데이터 있는 재료 비율 (시뮬레이터 신뢰도)
  const ingredientsWithAvg = item.ingredients.filter(ing => {
    const p = findPriceByName(prices, ing.name);
    if (!p) return false;
    const stat = priceAverages.get(p.code);
    return stat && stat.count > 0;
  }).length;
  const avgCoverage = item.ingredients.length > 0
    ? Math.round((ingredientsWithAvg / item.ingredients.length) * 100)
    : 0;

  return (
    <div style={{flex:1, padding: isMobile ? '16px' : '24px', overflowY:'auto'}}>

      {/* 모바일 뒤로가기 */}
      {isMobile && onBack && (
        <button onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: 'none', border: 'none', color: '#18181B',
            fontSize: '14px', fontWeight: '700', cursor: 'pointer',
            marginBottom: '14px', padding: '4px 0',
          }}>
          ‹ {t('목록으로')}
        </button>
      )}

      {/* 레시피 헤더 */}
      <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between',
                   marginBottom:'20px', flexWrap:'wrap', gap:'12px'}}>
        <div>
          <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
            <h2 style={{fontSize: isMobile ? '20px' : '22px', fontWeight:'700', color:'#18181B'}}>
              <BiName ko={item.name} zh={item.name_zh} size={isMobile ? 14 : 15} />
            </h2>
            <button onClick={onEdit}
              style={{padding:'5px 12px', borderRadius:'7px', border:'1px solid #E4E4E7',
                      background:'#fff', color:'#52525B', fontSize:'12px', fontWeight:'700',
                      cursor:'pointer'}}>
              {t('편집')}
            </button>
            {/* V150: 복사해서 새 버전 만들기 */}
            {onCopy && (
              <button type="button" data-recipe-copy onClick={onCopy}
                title={Z('이 레시피를 복사해서 조금만 고친 새 버전 만들기 (원본은 그대로)', '复制这个配方，稍微改一下做成新版本（原配方不变）')}
                style={{padding:'5px 12px', borderRadius:'7px', border:'1px solid #FDBA74',
                        background:'#FFF7ED', color:'#C2410C', fontSize:'12px', fontWeight:'700',
                        cursor:'pointer', whiteSpace:'nowrap'}}>
                📋 {Z('복사 · 새 버전', '复制 · 新版本')}
              </button>
            )}
            <button type="button" onClick={() => openPrint([item], { servings, title: item.name })}
              title={t('주방 벽에 붙이는 한중 병기 레시피 (원가 제외)')}
              style={{padding:'5px 12px', borderRadius:'7px', border:'1px solid #E4E4E7',
                      background:'#fff', color:'#52525B', fontSize:'12px', fontWeight:'700',
                      cursor:'pointer', whiteSpace:'nowrap'}}>
              🖨 {t('주방용 인쇄')}
            </button>
          </div>
          <span style={{display:'inline-block', padding:'3px 10px', borderRadius:'5px',
                        fontSize:'11px', fontWeight:'700', marginTop:'5px',
                        background: item.type === 'semi' ? '#dbeafe' : '#F4F4F5',
                        color: item.type === 'semi' ? '#18181B' : '#52525B'}}>
            {t(T.typeLabels[item.type] || item.type)}
          </span>
          {/* V150: 같은 원본에서 복사한 버전들 — 눌러서 바로 비교 */}
          {(() => {
            const rootId = familyRootId(item);
            const fam = (allRecipes || []).filter(r => r && r.id && familyRootId(r) === rootId);
            if (fam.length < 2) return null;
            const ordered = [...fam.filter(r => r.id === rootId), ...fam.filter(r => r.id !== rootId)];
            return (
              <div data-recipe-versions style={{display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:8}}>
                <span style={{fontSize:12, color:'#71717A', fontWeight:700}}>📋 {Z('버전', '版本')}</span>
                {ordered.map(r => {
                  const cur = r.id === item.id;
                  const label = lang === 'zh' ? (r.name_zh || r.name) : r.name;
                  return (
                    <button key={r.id} type="button" data-recipe-version={r.id} disabled={cur}
                      onClick={() => onOpenRecipe && onOpenRecipe(r.id)}
                      style={{padding:'3px 10px', borderRadius:999, fontSize:12, fontWeight:700,
                              border:`1.5px solid ${cur ? '#FF6F0F' : '#E4E4E7'}`, background: cur ? '#FFF4EC' : '#fff',
                              color: cur ? '#C2410C' : '#52525B', cursor: cur || !onOpenRecipe ? 'default' : 'pointer'}}>
                      {label}{r.id === rootId ? ` · ${Z('원본', '原版')}` : ''}
                    </button>
                  );
                })}
              </div>
            );
          })()}
          {item.note && (
            <p style={{fontSize:'13px', color:'#71717A', marginTop:'8px', whiteSpace:'pre-line'}}>{item.note}</p>
          )}
          {item.note && item.note_zh && (
            <p style={{fontSize:'13px', color:'#1A56A0', marginTop:'4px', whiteSpace:'pre-line'}}>{item.note_zh}</p>
          )}
        </div>

        {/* 인분/면적 조절 */}
        <div style={{display:'flex', alignItems:'center', gap:'10px',
                     background:'#FAFAFA', padding:'10px 16px', borderRadius:'10px',
                     border:'1px solid #F4F4F5'}}>
          <span style={{fontSize:'13px', color:'#27272A', fontWeight:'600'}}>{t(T.servingLabel)}</span>
          <QtyInput value={servings} onChange={v => setServings(Math.max(1, v))} min={1} />
          {T.lossRateEnabled && (
            <span style={{fontSize:'12px', color:'#71717A'}}>{T.servingUnit}</span>
          )}
        </div>
        <Hint hintKey="recipe_servings" arrow="top" mt={8} mb={0}
          zh={T.lossRateEnabled ? undefined : <>修改份数后<strong>成本会立即重新计算</strong>。录入售价后还能看到成本率和预计利润。</>}>
          {T.lossRateEnabled
            ? <>{T.servingLabel}을 바꾸면 <strong>자재비가 즉시 재계산</strong>돼요.</>
            : <>인분 수를 바꾸면 <strong>원가가 즉시 다시 계산</strong>돼요.
               판매가격도 입력해두면 원가율과 예상 이익까지 볼 수 있어요.</>
          }
        </Hint>
      </div>

      {/* V153: 반제품 이름이 식재료와 같을 때 — 경고 + 한 번에 고치기 */}
      <SemiClashCard key={item.id} item={item} allRecipes={allRecipes} prices={prices} onFix={onFixSemiClash} lastOrderOf={lastOrderOf} />

      {/* 원가 요약 카드 */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',
                   gap:'12px', marginBottom:'24px'}}>
        {[
          // V151: 인분 수를 바꾸면 총 생산량도 그만큼 (밑에 레시피 기준량). 인테리어(손실률)는 그대로
          (() => {
            const label = t(T.totalOutputLabel.replace(' (g)', '').replace(' (m²)', ''));
            const out = Number(item.total_output) || 0;
            if (T.lossRateEnabled || servings === 1 || out <= 0) return { label, value: T.totalOutputValue(item) };
            const scaled = Math.round(out * T.calcRatio(item, servings) * 10) / 10;
            return { label: `${label} · ${servingHead}`, value: T.totalOutputValue({ ...item, total_output: scaled }),
                     sub: `${t('기준량')} ${T.totalOutputValue(item)}`, key: 'output' };
          })(),
          { label: T.lossRateEnabled ? `기준 면적` : t('1인분'), value: T.servingBaseValue(item) },
          { label: lang === 'zh' && !T.lossRateEnabled ? `${servings}人份 成本` : T.costCardLabel(servings), value: money(costPerUnit * servings), highlight: true },
          servings > 1 ? { label: t(T.costCardSub), value: money(costPerUnit) } : null,
        ].filter(Boolean).map((card, i) => (
          <div key={i} style={{background: '#fff',
                               borderRadius:'10px', padding:'14px 16px',
                               border: '1px solid #F4F4F5'}}>
            <div style={{fontSize:'11px', color: '#A1A1AA',
                         marginBottom:'4px'}}>{card.label}</div>
            <div data-card={card.key || undefined} style={{fontSize:'18px', fontWeight:'800', color: '#18181B'}}>{card.value}</div>
            {card.sub && <div data-card-sub style={{fontSize:'11px', color:'#A1A1AA', marginTop:'3px'}}>{card.sub}</div>}
          </div>
        ))}
      </div>

      {/* 판매 메뉴 전용: 판매가 / 원재료율 / 이익 */}
      {item.type === 'menu' && (
        <div style={{
          background: sellingPrice > 0 ? '#fff' : '#FAFAFA',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '24px',
          border: `1.5px solid ${sellingPrice > 0 ? '#e5e7eb' : '#D4D4D8'}`,
        }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#52525B', marginBottom: '12px' }}>
            💰 {t('판매가 분석 (1인분 기준)')}
            {!sellingPrice && (
              <span style={{ fontSize: '12px', fontWeight: '400', color: '#A1A1AA', marginLeft: '8px' }}>
                — ✏️ {t('편집에서 판매가격을 입력하세요')}
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
            {/* 판매가격 */}
            <div style={{ background: '#F4F4F5', borderRadius: '10px', padding: '14px 16px' }}>
              <div style={{ fontSize: '11px', color: '#52525B', marginBottom: '4px' }}>{t('판매가격')}</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#52525B' }}>
                {sellingPrice > 0 ? money(sellingPrice) : t('미입력')}
              </div>
            </div>

            {/* 원재료율 — V73: 단일 톤 */}
            <div style={{
              background: '#FAFAFA',
              borderRadius: '10px', padding: '14px 16px',
              border: 'none',
            }}>
              <div style={{ fontSize: '11px', color: '#71717A', marginBottom: '4px' }}>{t('원재료율')}</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#18181B' }}>
                {costRatio !== null ? `${costRatio.toFixed(1)}%` : '-'}
              </div>
              {costRatio !== null && (
                <div style={{ fontSize: '11px', marginTop: '4px', color: '#71717A' }}>
                  {t(costRatio <= 30 ? '양호' : costRatio <= 40 ? '주의' : '위험')}
                </div>
              )}
            </div>

            {/* 1인분 원가 — V73: 동일 톤 */}
            <div style={{ background: '#FAFAFA', borderRadius: '10px', padding: '14px 16px',
                          border: 'none' }}>
              <div style={{ fontSize: '11px', color: '#71717A', marginBottom: '4px' }}>{t('1인분 원가')}</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#18181B' }}>
                {money(costPerUnit)}
              </div>
            </div>

            {/* 이익 */}
            <div style={{
              background: profit !== null && profit > 0 ? '#FAFAFA' : '#FAFAFA',
              borderRadius: '10px', padding: '14px 16px',
            }}>
              <div style={{ fontSize: '11px', color: '#71717A', marginBottom: '4px' }}>{t('1인분 이익')}</div>
              <div style={{ fontSize: '18px', fontWeight: '800',
                            color: profit !== null ? '#18181B' : '#A1A1AA' }}>
                {profit !== null ? money(profit) : '-'}
              </div>
            </div>
          </div>

          {/* ── 원가율 시뮬레이터 — V73: 테두리 제거, 옅은 BG */}
          {item.type === 'menu' && prices && prices.length > 0 && (
            <div style={{
              marginTop: 16, background: '#FAFAFA', borderRadius: 12, padding: '14px 16px',
              border: 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#18181B' }}>
                  {t('원가율 시뮬레이터')}
                </div>
                {avgCoverage > 0 && (
                  <span style={{ fontSize: 10, color: '#71717A' }}>
                    {Z(`영수증 평균가 데이터 ${avgCoverage}% 커버`, `小票平均价覆盖 ${avgCoverage}%`)}
                  </span>
                )}
              </div>

              {/* 단가 모드 토글 — V73: 활성만 다크, 비활성은 화이트 */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <button onClick={() => setPriceMode('registered')} type="button"
                  style={{
                    flex: 1, padding: '8px 6px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                    border: '1px solid #E4E4E7',
                    background: priceMode === 'registered' ? '#fff' : 'transparent',
                    color: priceMode === 'registered' ? '#18181B' : '#71717A',
                    cursor: 'pointer',
                  }}>{t('등록 단가')}</button>
                <button onClick={() => setPriceMode('avg30')} type="button"
                  disabled={avgCoverage === 0}
                  title={avgCoverage === 0 ? Z('영수증을 입력하면 활성화됩니다', '录入小票后可以使用') : Z('지난 30일 영수증 평균가', '最近 30 天小票平均价')}
                  style={{
                    flex: 1, padding: '8px 6px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                    border: '1px solid #E4E4E7',
                    background: priceMode === 'avg30' ? '#fff' : 'transparent',
                    color: priceMode === 'avg30' ? '#18181B' : '#71717A',
                    cursor: avgCoverage === 0 ? 'not-allowed' : 'pointer',
                    opacity: avgCoverage === 0 ? 0.5 : 1,
                  }}>{t('내 평균가 (30일)')}</button>
              </div>

              {/* 비교 카드 */}
              <div style={{ background: '#FAFAFA', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: '#71717A' }}>{Z('현재 1인분 원가', '现在每份成本')} ({priceMode === 'avg30' ? Z('평균가 기준', '按平均价') : Z('등록가 기준', '按登记价')})</span>
                  <strong style={{ color: '#18181B' }}>{money(costPerUnit)}</strong>
                </div>
                {priceMode === 'avg30' && Math.abs(costDiff) >= 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 2,
                                color: '#71717A', fontWeight: 700 }}>
                    <span>{Z('등록가 대비', '比登记价')} {costDiff > 0 ? '↑' : '↓'} {Math.abs(costDiffPct).toFixed(1)}%</span>
                    <span>{costDiff > 0 ? '+' : ''}{money(costDiff)}</span>
                  </div>
                )}
              </div>

              {/* 목표 원가율 → 권장 판매가 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, color: '#27272A', fontWeight: 600 }}>
                  {t('목표 원가율')}
                </label>
                <input type="number" value={targetCostRatio}
                  onChange={e => setTargetCostRatio(Number(e.target.value) || 0)}
                  min="10" max="80" step="1"
                  style={{
                    width: 60, padding: '6px 8px', border: '1.5px solid #FF6F0F',
                    borderRadius: 6, fontSize: 13, textAlign: 'right', fontWeight: 700,
                  }} />
                <span style={{ fontSize: 13, color: '#71717A' }}>%</span>
                <div style={{ flex: 1, minWidth: 140, textAlign: 'right' }}>
                  <span style={{ fontSize: 11, color: '#71717A', marginRight: 6 }}>{t('권장 판매가')}</span>
                  <strong style={{ fontSize: 16, color: '#18181B' }}>
                    {recommendedPrice !== null ? money(recommendedPrice) : '-'}
                  </strong>
                </div>
              </div>

              {/* 안내 */}
              {priceMode === 'avg30' && costDiff > costPerUnitRegistered * 0.05 && (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 7,
                  background: '#FAFAFA', border: '1px solid #E4E4E7',
                  fontSize: 11, color: '#52525B', fontWeight: 600,
                }}>
                  {Z('식자재 평균가가 등록가보다 비쌉니다. 목표 원가율 유지를 위해 판매가 인상 검토 필요.', '食材平均价比登记价高。要保持目标成本率，可以考虑调整售价。')}
                </div>
              )}
              {priceMode === 'registered' && avgCoverage === 0 && (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 7,
                  background: '#eff6ff', border: '1px solid #bfdbfe',
                  fontSize: 11, color: '#18181B',
                }}>
                  {Z('💡 영수증 OCR 입력 시 "내 평균가" 모드 활성화 — 실제 구매가 기반 시뮬 가능', '💡 录入小票后可以用「我的平均价」按实际进货价模拟')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 메뉴 가격 결정 도우미 (재료비 + 인건비 + 고정비 종합) ── */}
      {item.type === 'menu' && (
        <MenuPricingAdvisor
          materialCost={costPerUnit}
          sellingPrice={sellingPrice}
          profitLoss={profitLoss}
          payrollSettings={payrollSettings}
          onSettingsChange={onSettingsChange}
        />
      )}


      {/* 자재/재료 테이블 */}
      <div style={{background:'#fff', borderRadius:'12px', border:'1px solid #e5e7eb', overflow:'hidden'}}>
        <div style={{padding:'14px 18px', borderBottom:'1px solid #e5e7eb',
                     fontWeight:'700', color:'#18181B', fontSize:'15px'}}>
          {t(T.ingredient + ' 목록')}
        </div>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{width:'100%', borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'#FAFAFA'}}>
              {(T.lossRateEnabled
                ? [T.ingredient + '명', '기준량', '손실률', '실발주량', '단가', '비중'].map(h => t(h))
                // V124: 기준량(레시피 전체 분량)과 N인분을 나눠서 각각 원가를 보여준다.
                //       예전엔 "1g"(단위가 잘못 붙음) 옆 '단가'가 실제로는 기준량 전체 원가라 위 '1인분 원가'와 안 맞아 보였다.
                : [t(T.ingredient + '명'), t('기준량'), t('기준량 원가'), servingHead, `${servingHead} ${t('원가')}`, t('비중')]
              ).map((h, hi) => (
                <th key={hi} style={{padding: isMobile ? '10px 8px' : '10px 16px', textAlign: hi === 0 ? 'left' : 'right',
                                    fontSize:'12px', color:'#71717A', fontWeight:'600', whiteSpace: 'nowrap',
                                    borderBottom:'1px solid #e5e7eb'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {item.ingredients.map((ing, i) => {
              const ratio = T.calcRatio(item, servings);
              const cost = calcCost(ing);
              const baseQtyDisplay = T.lossRateEnabled
                ? `${ing.qty}${ing.unit ? ' ' + ing.unit : ''}`
                : `${ing.qty}${T.qtyUnit}`;
              const scaledQty = ing.qty * ratio;
              const actualQty = T.lossRateEnabled
                ? scaledQty * (1 + (ing.lossRate || 0) / 100)
                : scaledQty;
              const scaledQtyDisplay = T.lossRateEnabled
                ? `${actualQty.toFixed(2)}${ing.unit ? ' ' + ing.unit : ''}`
                : `${Math.round(scaledQty)}${T.qtyUnit}`;
              // V124: 식음료는 기준량 원가 × (N인분 g ÷ 총 생산량). 예전엔 기준량 원가 × N 이었다
              const scaledCost = cost > 0
                ? (T.lossRateEnabled ? cost * ratio * (1 + (ing.lossRate || 0) / 100) : cost * ratio)
                : 0;
              const cellPad = isMobile ? '10px 8px' : '10px 16px';
              return (
                <tr key={i} style={{borderBottom:'1px solid #f3f4f6',
                                    background: i % 2 === 0 ? '#fff' : '#fafafa'}}>
                  <td style={{padding: cellPad, fontSize:'13px', fontWeight:'500'}}>
                    <BiName ko={ing.name} zhMap={zhMap} />
                    {(() => {
                      // V141: 끊긴 반제품 — 이름을 바꾸기 전 옛 이름이 남은 경우 등
                      const key = normalizeIngredientName(ing.name);
                      const brk = brokenSemi.get(key);
                      if (!brk) return null;
                      const cands = brk.candidates.filter(c => c.id !== item.id);
                      const pick = relinkPick[key] !== undefined ? relinkPick[key] : (cands[0] && cands[0].score < 3 ? cands[0].id : '');
                      return (
                        <div data-broken-semi={ing.name} style={{ marginTop: 4, fontSize: 11, color: '#B45309', fontWeight: 600, lineHeight: 1.5 }}>
                          ⚠ {t('반제품 레시피를 찾을 수 없어요 (이름이 바뀌었나요?)')}
                          {onRelinkSemi && cands.length > 0 && (
                            <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                              <select value={pick} onChange={e => setRelinkPick(m => ({ ...m, [key]: e.target.value }))}
                                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid #FDBA74', maxWidth: 220 }}>
                                {!pick && <option value="">{t('반제품 고르기')}</option>}
                                {cands.map(c => <option key={c.id} value={c.id}>{c.name}{c.name_zh ? ` · ${c.name_zh}` : ''}</option>)}
                              </select>
                              <button type="button" disabled={!pick} onClick={() => relink(ing.name, pick, brk.usedIn)}
                                style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, border: 'none', fontWeight: 700,
                                         background: pick ? '#EA580C' : '#E4E4E7', color: '#fff', cursor: pick ? 'pointer' : 'default' }}>
                                {t('이 반제품으로 연결')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{padding: cellPad, fontSize:'13px', textAlign:'right', color:'#71717A', whiteSpace:'nowrap'}}>
                    {baseQtyDisplay}
                  </td>
                  {/* V124: 기준량 원가 (레시피 전체 분량 기준) */}
                  {!T.lossRateEnabled && (
                    <td style={{padding: cellPad, fontSize:'13px', textAlign:'right', whiteSpace:'nowrap',
                                 color: cost > 0 ? '#71717A' : '#A1A1AA'}}>
                      {cost > 0 ? money(cost) : isFreeIng(ing)
                        ? <>{money(0)} <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 700 }}>{t('무료')}</span></>
                        : t('단가 없음')}
                    </td>
                  )}
                  {T.lossRateEnabled && (
                    <td style={{padding:'10px 16px', fontSize:'13px', textAlign:'right',
                                 color: (ing.lossRate || 0) > 0 ? '#A1A1AA' : '#E4E4E7'}}>
                      {(ing.lossRate || 0) > 0 ? `+${ing.lossRate}%` : '—'}
                    </td>
                  )}
                  <td style={{padding: cellPad, fontSize:'13px', textAlign:'right', color:'#18181B', fontWeight:'600', whiteSpace:'nowrap'}}>
                    {scaledQtyDisplay}
                  </td>
                  <td style={{padding: cellPad, fontSize:'13px', textAlign:'right', whiteSpace:'nowrap',
                               color: scaledCost > 0 ? '#18181B' : '#A1A1AA', fontWeight: scaledCost > 0 ? '600' : '400'}}>
                    {scaledCost > 0 ? money(scaledCost) : isFreeIng(ing) ? money(0) : (T.lossRateEnabled ? t('단가 없음') : '—')}
                  </td>
                  {(() => {
                    const pct = totalScaledCost > 0 && scaledCost > 0
                      ? (scaledCost / totalScaledCost * 100)
                      : 0;
                    // V72: 단일 색상 (다크 그레이) — 비중도 단순 텍스트
                    return (
                      <td style={{padding: cellPad, fontSize:'13px', textAlign:'right',
                                   color: pct === 0 ? '#A1A1AA' : '#71717A', fontWeight: 700}}>
                        {pct > 0 ? (pct < 0.5 ? '<1%' : `${Math.round(pct)}%`) : '—'}
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{background:'#FAFAFA'}}>
              <td colSpan={T.lossRateEnabled ? 4 : 2} style={{padding: isMobile ? '12px 8px' : '12px 16px', fontWeight:'700', color:'#18181B', whiteSpace:'nowrap'}}>
                {t('합계')} ({item.ingredients.length})
              </td>
              {/* V124: 기준량 원가 합계 (레시피 전체) */}
              {!T.lossRateEnabled && (
                <td style={{padding: isMobile ? '12px 8px' : '12px 16px', textAlign:'right', fontWeight:'700',
                             color:'#52525B', fontSize:'14px', whiteSpace:'nowrap'}}>
                  {money(totalCost)}
                </td>
              )}
              {!T.lossRateEnabled && <td />}
              <td style={{padding: isMobile ? '12px 8px' : '12px 16px', textAlign:'right', fontWeight:'800',
                           color:'#18181B', fontSize:'15px', whiteSpace:'nowrap'}}>
                {(() => {
                  const ratio = T.calcRatio(item, servings);
                  // 식음료: N인분 원가 (= 위 'N인분 원가' 카드와 같은 값), 인테리어: 손실률 반영 실발주 자재비
                  const displayTotal = T.lossRateEnabled
                    ? item.ingredients.reduce((sum, ing) => {
                        const c = calcCost(ing);
                        return sum + c * ratio * (1 + (ing.lossRate || 0) / 100);
                      }, 0)
                    : totalCost * ratio;
                  return money(displayTotal);
                })()}
              </td>
              <td style={{padding: isMobile ? '12px 8px' : '12px 16px', textAlign:'right', fontWeight:'800',
                           color:'#18181B', fontSize:'14px'}}>
                {totalScaledCost > 0 ? '100%' : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>

      {toolbar && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #E4E4E7' }}>{toolbar}</div>
      )}
      {picker}
    </div>
  );
}

export default RecipeView;